<template>
  <div class="row primary-filter">
    <div class="col-md-auto filter-grid">
      <ul class="inlinelist">
        <li class="app-side-menu">
          <span @click="isOpen = true" class="pointer">
            <svgicon name="menu" width="20" height="20" :original="true" class="menu" />
          </span>
          <div class="sidemenu-slideout" :class="{ isOpen: isOpen }">
            <sidebar @close="isOpen = false" />
          </div>
        </li>
        <li class="color-changes category">
          <filter-dropdown
            v-if="menuOptions.length"
            :options="menuOptions"
            :selected="selectedCategory"
            @updateOption="onChangeOption" />
        </li>
      </ul>
    </div>
    <div class="col filter-grid">
      <div class="row" v-if="!noSearchFilter">
        <div class="col">
          <div class="app-search-label">
            <div v-if="showPopup">
              <search-popup
                :searchParameters="searchParameters"
                :presearchedParams="preSearchedParams"
                @closePopup="closePopup"
                @search="searchByParameter" />
            </div>
            <div class="searchterm tagblock" v-if="!showPopup" @click="onClick"
              :class="{ 'pointer' : noParams}">
              <svgicon name="search" width="15" height="15" :original="true" />
              <input type="text" :placeholder="searchText" @keyup="search" @input="validateInput"
                v-model="query" v-if="!enableSearchPopup">
              <span type="text" class="tagblock_list" v-else>
                <span v-for="(value, name, index) in searchedParams" :key="index">
                  <span v-if="value">
                    <tag closable @close="removeParamTag(name)">
                      <div class="searchtag-title">{{ getTagByName(name) }}</div>
                      {{ value }}
                    </tag>
                  </span>
                </span>
                <span v-if="noParams" class="inputtextlabel">{{searchText}}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-auto filter-grid">
        <FilterDropdown
          v-if="languages.length > 1 && selectedLanguage"
          :options="languages"
          :selected="selectedLanguage"
          v-on:updateOption="updateLanguage"
        />
     </div>
    <div class="col-md-auto filter-grid filter-button">
      <span>
        <ul class="filter-btn-group inlinelist">
          <li v-for="button in buttonsList" :key="button.id">
            <span v-if="button.show && showButton(button)">
              <router-link
                v-if="button.routerLinkName"
                :to="{ name: button.routerLinkName, query : {ids: queryParams}}">
                <button class="btn" :class="button.class">{{button.name}}</button>
              </router-link>
              <tooltip
                v-else-if="button.disableTooltip && button.disabled"
                effect="dark"
                placement="bottom-start">
                <div slot="content">{{button.disableTooltip}}</div>
                <span class="pointer">
                  <button class="btn" :class="getClass(button)" :disabled="button.disabled"
                    @click="$emit(button.emitMethod)">{{button.name}}</button>
                </span>
              </tooltip>
              <dropdown
                v-else-if="button.dropdownList && !isUploading "
                trigger="click"
                @visible-change="toggleButton(button.id)"
                :class="{dropdownbutton : dropdownOpen && dropdownOpen[button.id]}">
                <button type="primary" class="btn btn-secondary" :class="button.class">
                  {{button.name}}
                  <svgicon name="categorydropdown" class="dropdown-icon" width="10"
                    height="10" :original="true"/>
                </button>
                <dropdown-menu slot="dropdown" :class="button.dropdownClass">
                  <dropdown-item v-for="(dropdownItem,index) in button.dropdownList" :key="index">
                    <span v-if="dropdownItem.type === 'upload' &&
                      (!dropdownItem.permission || $hasPermission(dropdownItem.permission)) &&
                       (!dropdownItem.config || (hasConfiguration(dropdownItem, ''))) ">
                      <label>
                        <input class="upload-material" type="file" id="file"
                          :ref="dropdownItem.uploadEmitMethod"
                          :accept="`${AttachmentConstants.excelMimes}`"
                          @change="handleFileUpload(dropdownItem.uploadEmitMethod)"/>
                        <span class="action">{{dropdownItem.name}}</span>
                      </label>
                      <a :href="`${$asset_path}static/sample/${dropdownItem.sampleFileName}`"
                        v-if="dropdownItem.sampleFileName"
                        download
                        class="format-text"
                        target="_blank">Sample format</a>
                      <a href="javascript:void(0)" v-else
                        @click="$emit(dropdownItem.sampleFileEmitMethod)"
                        class="format-text">Sample format</a>
                    </span>
                    <span v-if="dropdownItem.type === 'link' &&
                      hasConfiguration(dropdownItem, button.parentConfig)">
                      <label>
                        <li class="select_options">
                          <router-link v-if="dropdownItem.routerLinkName"
                            :to="getRouterLink(dropdownItem)">
                            <a href="#" class="action">{{dropdownItem.name}}</a>
                          </router-link>
                        </li>
                      </label>
                    </span>
                  </dropdown-item>
                </dropdown-menu>
              </dropdown>
              <processing background="#0073FF" size="30px"
                v-else-if="button.dropdownList && isUploading" />
              <button v-else class="btn" :class="getClass(button)" :disabled="button.disabled"
                @click="$emit(button.emitMethod)">{{button.name}}</button>
            </span>
          </li>
        </ul>
      </span>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import debounce from 'debounce';
import FilterDropdown from '@common/components/FilterDropdown';
import SearchPopup from '@common/components/SearchPopup';
import AttachmentConstants from '@common/constants/AttachmentConstants';
import Sidebar from '@/modules/common/Sidebar';
import setLocale from '@/utils/localisation';

export default {
  props: {
    searchText: {
      type: String,
      default: 'Search',
    },
    menuOptions: {
      type: Array,
      required: true,
    },
    selectedCategory: {
      type: Object,
      required: true,
    },
    queryParams: {
      type: String,
      default: '',
    },
    buttonsList: {
      type: Array,
      default: () => [],
    },
    noSearchFilter: {
      type: Boolean,
      default: false,
    },
    isUploading: {
      type: Boolean,
      default: false,
    },
    enableSearchPopup: {
      type: Boolean,
      default: false,
    },
    searchParameters: {
      type: Array,
      default: () => [],
    },
    preSearchedParams: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    Sidebar,
    FilterDropdown,
    SearchPopup,
  },
  data() {
    return {
      query: null,
      isOpen: false,
      focusSearch: true,
      dropdownOpen: null,
      AttachmentConstants,
      showPopup: false,
      searchedParams: {},
      selectedLanguage: null,
    };
  },
  computed: {
    ...mapGetters({
      configurations: 'app/currentClientConfig',
    }),
    noParams() {
      return Object.values(this.searchedParams).every(parameter => !parameter);
    },
    showButton() {
      return (button) => {
        if (button.dropdownList) {
          if (button.dropdownList.some(dropdownItem =>
            dropdownItem.type === 'upload' &&
        (!dropdownItem.permission || this.$hasPermission(dropdownItem.permission)) &&
        (!dropdownItem.config || this.hasConfiguration(dropdownItem, '')))) {
            return true;
          }
          return false;
        }
        return true;
      };
    },
    languages() {
      const locales =
        (this.configurations &&
          this.configurations.locales &&
          this.configurations.locales.allowed_locales) ||
        [];
      return locales;
    },
    defaultLanguage() {
      const preSelectedLocale = localStorage.getItem('tsm-transporter-locale');
      const defaultLocale =
        this.configurations &&
        this.configurations.locales &&
        this.configurations.locales.default_locale;
        const localeName = this.configurations && this.configurations.locales && this.configurations.locales.allowed_locales.find(locale=>locale.id === (preSelectedLocale || defaultLocale)).name
      return preSelectedLocale || localeName;
    },
  },
  created() {
    if (this.preSearchedParams) {
      this.searchParameters.map((searchParameter) => {
        Vue.set(
          this.searchedParams,
          searchParameter.query,
          this.preSearchedParams[searchParameter.query],
        );
      });
    }
    this.loadSearchFromSession();
    this.setDropDownOpenConstants();
    this.selectedLanguage = this.defaultLanguage;

  },
  methods: {
    async updateLanguage(val) {
      localStorage.setItem('tsm-transporter-locale', val.id);
      await setLocale(val.id);
    },
    validateInput(evt) {
      this.query = evt.target.value.replace(/[^a-z0-9+@\-_ .]/gi, '');
    },
    loadSearchFromSession() {
      if (!this.enableSearchPopup) {
        const sessionQuery = localStorage.getItem(`${this.$route.name}-search-query`);
        if (sessionQuery) {
          this.query = sessionQuery;
          this.search();
        }
      }
    },
    setSearchToSession() {
      if (!this.enableSearchPopup) {
        localStorage.setItem(`${this.$route.name}-search-query`, this.query || '');
      }
    },
    toggleButton(id) {
      const obj = {};
      obj[id] = !this.dropdownOpen[id];
      this.dropdownOpen = Object.assign({}, this.dropdownOpen, obj);
    },
    setDropDownOpenConstants() {
      this.dropdownOpen = {};
      this.buttonsList.forEach((val) => {
        if (val.dropdownList) {
          this.dropdownOpen[val.id] = false;
        }
      });
    },
    hasConfiguration(value, parentConfig) {
      let configForUser = this.configurations;
      if (parentConfig) {
        configForUser =
          this.configurations && this.configurations[parentConfig];
      }
      return configForUser && configForUser[value.config];
    },
    getRouterLink(value) {
      let routerLink = { name: value.routerLinkName };
      if (value.queryParams) {
        routerLink = Object.assign(routerLink, { query: value.queryParams });
      }
      return routerLink;
    },
    unCheck(value) {
      this[value] = null;
    },
    getTagByName(name) {
      return this.searchParameters.find(param => param.query === name).name;
    },
    removeParamTag(tag) {
      this.unCheck(tag);
      this.searchedParams[tag] = null;
      this.searchByParameter(this.searchedParams);
    },
    searchByParameter(query, isReset = false) {
     
      // used to display the searched queries when the pop up is reopened
      this.searchedParams = query;
      this.$emit('searchByParameter', query);
      if (!isReset) {
        this.closePopup();
      }
    },
    onClick() {
      this.focusSearch = false;
      if (this.enableSearchPopup) {
        this.showPopup = true;
      }
    },
    closePopup() {
      this.showPopup = false;
    },
    search: debounce(function debounceSearch() {
      this.setSearchToSession();
      if (this.query) {
        this.$emit('search', this.query);
      }
    }, 500),
    onChangeOption(data) {
      if (this.$router.history.current.name === 'OrdersList') {
        this.$emit('changeCategory', data);
      } else {
        this.$router.push({ name: data.id });
      }
    },
    getClass(button) {
      let className = button.class;
      if (button.disabled) {
        className = className.concat(' disablebtn ');
      }
      return className;
    },
    handleFileUpload(emitMethod) {
      this.file = this.$refs[emitMethod][0].files[0];
      this.$emit(emitMethod, this.file);
      // Reset the uploaded file value
      const input = this.$refs[emitMethod][0];
      input.type = 'file';
    },
  },
};
</script>
<style lang="scss">
@import "~@common/styles-transporter/settings";
$fontSemibold: 'OpenSans-Semibold';
$fontBold: 'OpenSans-Bold';
.primary-filter {
  border: 1px solid #E9F1F5;
  background-color: #fff;
  height: 50px;
  font-family:$fontSemibold;
  .dropdown-menu {
    top: 32px !important;
  }
  .filter-grid {
    padding-left: 0;
    padding-right: 0;
    // border-left: 1px solid #CBD4D8;
    ul {
      list-style: none;
      li {
        //display: inline-block;
      }
      .app-side-menu {
        // background-color: #F2F1F1;
        padding: 14px 0;
        width: 69px;
        text-align: center;
        position: relative;
        top: -2px;
        //background: #F2F1F1;
        cursor: pointer;
      }
      .category {
        // width: 151px;
        // padding: 4px 10px;
        color: #007FEA;
        // padding-left: 10px;
        border-right: 1px solid #E9F1F5;
        border-left: 1px solid #E9F1F5;
        font-size: 1.4rem;
        text-transform: uppercase;
        margin-left: -3px;
        position: relative;
        top: -1px;
        height: 49px;
        .dropdown {
          //padding: 15px 20px;
          min-width: 200px;
        }
      }
      .search {
        padding: 0 20px;
        width: 50%;
        svg {
          position: relative;
          top: 0px;
        }
        input {
          outline: none;
          border: 1px solid transparent;
          padding: 0 5px;
          width: 90%;
          font-size: 1.4rem;
        }
      }
    }
    &.filter-button {
      ul {
        @extend %list-unstyled;
        float: right;
        padding: 9.5px 10px;
        li {
          margin: 0 0 0 18px;
          &:last-child {
            margin: 0 0 0 10px;
          }
          .btn-primary {
            @extend %header-primary-btn;
            @extend %bold;
            color: #fff;
            text-transform: uppercase;
          }
          .btn-secondary {
            @extend %header-secondary-btn;
            @extend %bold;
            color: #008AFF;
            text-transform: uppercase;
          }
        }
      }
    }
  }
}
.select_options{
  a{
    text-decoration: none;
  }
}
.action{
  text-decoration: none;
}
.inboundbutton{
      width: 142px !important;
}
.inbounddropdown.el-dropdown-menu.el-popper{
      width: 142px !important;
}
.inbounddropdown .el-dropdown-menu__item{
    margin: 0 0 0 0px !important;
    padding: 5px 10px !important;
}
</style>
